// Service-worker registration + Web-Push subscription seam. The real VAPID key is supplied
// by D1 (<meta name="beacon-vapid">) and the subscription is consumed by A6; absent a key
// this is a no-op and alerting falls back to in-app toasts (mock-register Web Push row).

export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function registerServiceWorker(
  deps: { navigator?: Navigator } = {},
): Promise<ServiceWorkerRegistration | null> {
  const nav = deps.navigator ?? (typeof navigator !== "undefined" ? navigator : undefined);
  if (!nav || !("serviceWorker" in nav)) return null;
  try {
    // updateViaCache:"none" → the browser always revalidates /sw.js (never serves it
    // from the HTTP cache during update checks), so a new deploy is detected promptly.
    return await nav.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
  } catch {
    return null; // registration failure -> in-app-only alerting (graceful)
  }
}

/** Silent auto-update: reload the open tab once a freshly-deployed service worker
 *  takes control, and poll for new deploys so an already-open tab upgrades itself
 *  without a manual navigation. The SW calls skipWaiting()+clients.claim(), so a new
 *  version activates immediately; this just refreshes the running page onto it. */
export function enableAutoUpdate(
  reg: Pick<ServiceWorkerRegistration, "update">,
  deps: {
    navigator?: Navigator;
    reload?: () => void;
    setInterval?: (fn: () => void, ms: number) => unknown;
    intervalMs?: number;
  } = {},
): void {
  const nav = deps.navigator ?? (typeof navigator !== "undefined" ? navigator : undefined);
  if (!nav || !("serviceWorker" in nav)) return;
  const reload = deps.reload ?? (() => location.reload());
  const schedule = deps.setInterval ?? ((fn, ms) => setInterval(fn, ms));
  const intervalMs = deps.intervalMs ?? 60_000;

  // Reload once when a new SW takes control — but NOT on the very first install
  // (no prior controller), which would needlessly reload the initial page load.
  const hadController = !!nav.serviceWorker.controller;
  let reloading = false;
  nav.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading || !hadController) return;
    reloading = true;
    reload();
  });

  // Poll for a newly-deployed /sw.js so a tab that stays open across a deploy still
  // upgrades itself. wallclock-allow: browser update cadence (gate-30 excludes apps/webapp).
  schedule(() => { void reg.update(); }, intervalMs);
}

/** Real pushManager.subscribe against a D1-supplied VAPID key. Returns null (in-app fallback)
 *  when no key is configured or the user denies — never throws. A6 stores the subscription. */
export async function subscribeForPush(
  reg: ServiceWorkerRegistration,
  vapidPublicKey: string | null,
): Promise<PushSubscription | null> {
  _lastSubscribeError = null; // fresh attempt — clear any prior error (incl. the no-key no-op)
  if (!vapidPublicKey || !("pushManager" in reg)) return null;
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) return existing;
    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // cast: TS 5.7 types TypedArrays over ArrayBufferLike; applicationServerKey wants a
      // BufferSource backed by ArrayBuffer (never SharedArrayBuffer) — the bytes are.
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
  } catch (err) {
    // A VAPID key WAS present but the platform rejected the subscribe (e.g. iOS Safari
    // outside an installed PWA, Android Firefox autopush unavailable, push-service
    // error). Capture it: on a device with no remote debugging this is the only signal,
    // and it's how the diagnostic toast tells "subfail" from "no key".
    _lastSubscribeError = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    console.warn("[push] pushManager.subscribe failed", err);
    return null;
  }
}

// Last pushManager.subscribe() error, surfaced on-device via the diagnostic toast
// (toast-logging — no console access in an installed PWA). Reset on every attempt.
let _lastSubscribeError: string | null = null;
export function lastSubscribeError(): string | null {
  return _lastSubscribeError;
}

/** True when a subscribe() error means the device/browser has NO push delivery service
 *  rather than a transient or permission problem — e.g. a de-Googled Android (no Google
 *  Play Services / FCM), or Firefox whose autopush transport can't register. Web Push
 *  simply cannot work there until a non-FCM path (UnifiedPush) or a native app ships, so
 *  the UI should say that plainly instead of "try again". Gecko reports it as
 *  `AbortError: Error retrieving push subscription`; some builds as "push service not
 *  available". Matched case-insensitively on the captured "name: message" string. */
export function isNoPushServiceError(error: string | null): boolean {
  if (!error) return false;
  const e = error.toLowerCase();
  return (
    e.includes("aborterror") ||
    e.includes("retrieving push subscription") ||
    e.includes("push service not available") ||
    e.includes("registration failed")
  );
}

/** One-line push diagnostic for the on-device toast: permission, whether the page
 *  carries a VAPID key, whether we got creds, and the raw subscribe() error if any. */
export function pushDiagText(d: {
  permission: NotificationPermission | "unsupported";
  hasKey: boolean;
  hasCreds: boolean;
  subscribeError: string | null;
}): string {
  const parts = [`perm=${d.permission}`, `key=${d.hasKey ? "yes" : "no"}`, `push=${d.hasCreds ? "ok" : "no"}`];
  if (d.subscribeError) parts.push(`err: ${d.subscribeError}`);
  return parts.join(" · ");
}

export function vapidKeyFromMeta(doc: Document = document): string | null {
  const meta = doc.querySelector<HTMLMetaElement>('meta[name="beacon-vapid"]');
  const v = meta?.content?.trim();
  return v ? v : null;
}

/** Pure mapping from (permission, page-has-vapid-key, got-creds) to a toast outcome,
 *  so the user always learns WHY enabling notifications did or didn't take. Splits the
 *  two granted-but-no-push cases: `nopush` (no VAPID key visible to this client — server
 *  not configured, or a stale cached shell) vs `subfail` (key present, but the device's
 *  pushManager.subscribe produced nothing; see the console warning above). */
export type NotifOutcome = "enabled" | "denied" | "unsupported" | "nopush" | "subfail" | "dismissed";
export function notifOutcome(
  permission: NotificationPermission | "unsupported",
  hasKey: boolean,
  hasCreds: boolean,
): NotifOutcome {
  if (permission === "unsupported") return "unsupported";
  if (permission === "denied") return "denied";
  if (permission === "default") return "dismissed";
  if (hasCreds) return "enabled";
  return hasKey ? "subfail" : "nopush";
}

/** Request OS notification permission. MUST be called synchronously inside a user
 *  gesture — browsers suppress the prompt if it is awaited behind other async work,
 *  which is exactly why opting in used to fail silently (no prompt, no subscription).
 *  Re-uses an existing decision ("granted"/"denied") without re-prompting; only the
 *  "default" (never-asked) state shows the prompt. Returns "unsupported" when the
 *  Notification API is absent. Never throws. */
export async function requestNotificationPermission(
  deps: { notification?: typeof Notification } = {},
): Promise<NotificationPermission | "unsupported"> {
  const N = "notification" in deps ? deps.notification : (typeof Notification !== "undefined" ? Notification : undefined);
  if (!N) return "unsupported";
  if (N.permission === "granted" || N.permission === "denied") return N.permission;
  try {
    return await N.requestPermission();
  } catch {
    return "denied";
  }
}
